import * as z from "zod";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Recipients } from "api/types/device";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../providers/form";
import { useTranslation } from "react-i18next";
import GenericButton from "widgets/controls/buttons/generic";
import ComboBox from "widgets/shared/inputs/combobox";
import { shallowEqual, useSelector } from "react-redux";
import { rateStructuresList } from "store/appSelectors";
import { ServiceLevelDropdown } from "models/dropdowns/serviceLevel";
import { BillingPeriodDropdown } from "models/dropdowns/billingPeriod";
import { useEffect, useState } from "react";
import { CollectorDropdown } from "models/dropdowns/collector";
import { usePermissions } from "hooks/usePermissions";
import { CONST } from "models/constants";
import TimePicker from "widgets/shared/inputs/timePicker";
import Switch from "widgets/shared/inputs/switch";
import LabeledField from "providers/labeledField";
import CircleButton from "widgets/controls/buttons/circle-button";
import { MdAdd, MdClose } from "react-icons/md";

// Recipients schema
const RecipientsSchema = z.object({
  email: z.string(),
  phone: z.string(),
  username: z.string(),
  missing: z.string(),
  name: z.string(),
  platform: z.string(),
});

// Schema
const SiteConfigurationSchema = z.object({
  rateStructureId: z.string().optional(),
  serviceLevel: z.string().optional(),
  billingPeriod: z.string().optional(),
  collector: z.string().optional(),
  escalationConfig: z.object({
    enabled: z.boolean().optional(),
    levels: z.array(z.object({
      index: z.number().optional(),
      time: z.number().optional(),
      description: z.string().optional(),
      actions: z.array(z.string()).optional(),
      recipients: z.array(RecipientsSchema).optional(),
    }).optional()).optional(),
  }).optional(),
});

export type SiteConfigurationFormType = z.infer<typeof SiteConfigurationSchema>;

// Props
type SiteConfigurationFormProps = {
  submit: (data: SiteConfigurationFormType) => void;
  onClose: () => void;
  defaultValues?: SiteConfigurationFormType;
  values?: SiteConfigurationFormType;
  loading?: boolean;
  disabled?: boolean;
  context?: "site" | "escalation";
  readonly?: boolean;
};

const SiteConfigurationForm = ({
  submit,
  onClose,
  defaultValues,
  values,
  loading,
  disabled,
  context = "site",
  readonly = false,
}: SiteConfigurationFormProps) => {
  const form = useForm<SiteConfigurationFormType>({
    resolver: zodResolver(SiteConfigurationSchema),
    defaultValues: {
      rateStructureId: "",
      serviceLevel: "",
      billingPeriod: "",
      collector: "",
      escalationConfig: {
        enabled: false,
        levels: [],
      },
    },
    values,
  });

  const ratesStructureList = useSelector(rateStructuresList, shallowEqual);
  const { t } = useTranslation();
  const { canShow } = usePermissions();
  const { errors, isDirty } = form.formState;
  
  // Get current escalation levels
  const escalationLevels = form.watch("escalationConfig.levels") || [];
  const [levelCount, setLevelCount] = useState(escalationLevels.length || 0);

  const clearErrors = () => {
    form.clearErrors();
  };

  // Add a new escalation level
  const addEscalationLevel = () => {
    const newLevelCount = levelCount + 1;
    setLevelCount(newLevelCount);
    
    // Ensure the levels array exists
    const currentLevels = form.getValues("escalationConfig.levels") || [];
    const updatedLevels = [...currentLevels];
    
    // Add new level if it doesn't exist
    if (updatedLevels.length < newLevelCount) {
      updatedLevels.push({ 
        index: newLevelCount, 
        time: 0,
      });
    }
    
    form.setValue("escalationConfig.levels", updatedLevels);
  };

  // Remove the last escalation level
  const removeEscalationLevel = () => {
    if (levelCount > 0) {
      const newLevelCount = levelCount - 1;
      setLevelCount(newLevelCount);
      
      const currentLevels = form.getValues("escalationConfig.levels") || [];
      const updatedLevels = currentLevels.slice(0, newLevelCount);
      
      form.setValue("escalationConfig.levels", updatedLevels);
    }
  };

  // Automatically submit the form if valid
  const onSubmit = async (formData: SiteConfigurationFormType) => {
    try {
      if (formData.escalationConfig?.levels) {
        formData.escalationConfig.levels = formData.escalationConfig.levels.map((level, idx) => ({
          ...level,
          index: idx + 1,
          description: level.description || `Level ${idx + 1}`,
          actions: level.actions || [],
          recipients: level.recipients || []
        }));
      }
      await submit(formData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  useEffect(() => {
    if (disabled && isDirty) {
      form.reset(values || defaultValues);
    }
  }, [disabled, isDirty, form, values, defaultValues]);

  // Initialize level count from values
  useEffect(() => {
    if (values?.escalationConfig?.levels) {
      setLevelCount(values.escalationConfig.levels.length);
    }
  }, [values]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
        {context === "site" && (
          <>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
            <FormField
              control={form.control}
              name="rateStructureId"
              rules={{ required: false }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {"Rates Structure"}
                  </FormLabel>
                  <FormControl>
                    <ComboBox
                      items={ratesStructureList || []}
                      placeholder={t("form.placeholder.site.billing.period")}
                      labelKey="name"
                      valueKey="id"
                      id="rate-structure-id"
                      type="text"
                      state={errors.rateStructureId ? "error" : undefined}
                      onFocus={clearErrors}
                      disabled={disabled || !canShow(CONST.ROLE_OWNER)}
                      clearIcon={false}
                      filter
                      color="secondary"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="collector"
              rules={{ required: false }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{"Collector"}</FormLabel>
                  <FormControl>
                    <ComboBox
                      items={CollectorDropdown || []}
                      placeholder={t("form.placeholder.site.billing.period")}
                      labelKey="label"
                      valueKey="value"
                      id="collector"
                      type="text"
                      state={errors.collector ? "error" : undefined}
                      onFocus={clearErrors}
                      disabled={disabled || !canShow(CONST.ROLE_ADMINISTRATOR)}
                      clearIcon={false}
                      color="secondary"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
            <FormField
              control={form.control}
              name="serviceLevel"
              rules={{ required: false }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("form.input.site.service.level")}
                  </FormLabel>
                  <FormControl>
                    <ComboBox
                      items={ServiceLevelDropdown}
                      placeholder={t("form.placeholder.site.service.level")}
                      labelKey="label"
                      valueKey="value"
                      id="service-level"
                      type="text"
                      state={errors.serviceLevel ? "error" : undefined}
                      onFocus={clearErrors}
                      disabled={disabled || !canShow(CONST.ROLE_ADMINISTRATOR)}
                      clearIcon={false}
                      color="secondary"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="billingPeriod"
              rules={{ required: false }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("form.input.site.billing.period")}
                  </FormLabel>
                  <FormControl>
                    <ComboBox
                      items={BillingPeriodDropdown}
                      placeholder={t("form.placeholder.site.billing.period")}
                      labelKey="label"
                      valueKey="value"
                      id="billing-period"
                      type="text"
                      state={errors.billingPeriod ? "error" : undefined}
                      onFocus={clearErrors}
                      disabled={disabled || !canShow(CONST.ROLE_ADMINISTRATOR)}
                      clearIcon={false}
                      color="secondary"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          </>
        )}
        {context === "escalation" && (
          <>
          {!readonly && <>
          <div className="flex justify-end -mt-8">
            <FormField
              control={form.control}
              name="escalationConfig.enabled"
              rules={{ required: false }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel></FormLabel>
                  <FormControl>
                    <Switch
                      labelKey={'Enable'}
                      returnBoolean
                      checked={!!field.value}
                      onChange={(val: any) => {
                        field.onChange(val);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex">
                {t("form.input.site.escalation.description")}
              </div>
          
          <div className="flex justify-end mb-4">
            <div className="flex gap-2">
              <CircleButton
                icon={MdAdd}
                onClick={addEscalationLevel}
                disabled={disabled}
                size="sm"
                variant="solid"
                color="tertiary"
              />
              <CircleButton
                icon={MdClose}
                onClick={removeEscalationLevel}
                disabled={disabled || levelCount <= 0}
                size="sm"
                variant="solid"
                color="tertiary"
              />
            </div>
          </div>
          </>}
          
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-5">
            {Array.from({ length: levelCount }, (_, index) => (
              <FormField
                key={`escalationConfig-levels-${index}-time`}
                control={form.control}
                name={`escalationConfig.levels.${index}.time`}
                rules={{ required: false }}
                render={({ field }) => {
                  // Convert string ("HH:mm") to { hour, minute }
                  const value = typeof field.value === "number"
                    ? { hour: Math.floor(field.value / 60), minute: field.value % 60 }
                    : { hour: 0, minute: 0 };

                  return (
                    <FormItem>
                      <FormLabel>
                        {`${t("form.input.site.escalation.time", {context: index + 1})}`}
                      </FormLabel>
                      <FormControl>
                        <TimePicker
                          id={`time-picker-${index}`}
                          placeholder={t("form.placeholder.site.service.level")}
                          value={value}
                          onChange={({ hour, minute }) => {
                            field.onChange(hour * 60 + minute);
                          }}
                          disabled={disabled || readonly}
                          clearIcon={false}
                          color="secondary"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            ))}
          </div>
          </>
        )}
        <div className="flex justify-end py-2">
          <div className="flex gap-2">
            {!readonly ? 
            <>
            <GenericButton
              onClick={onClose}
              text={t("button.action.cancel")}
              size="md"
              variant="ghost"
              color="secondary"
            />

            <GenericButton
              type="submit"
              text={t("button.action.save")}
              loading={loading || false}
              size="md"
            />
            </> :
            <GenericButton
              text={t("button.action.ok")}
              loading={loading || false}
              size="md"
              onClick={onClose}
            />}
          </div>
        </div>
      </form>
    </Form>
  );
};

export default SiteConfigurationForm;
